import React, { useEffect, useState, useCallback } from 'react';
import { RouterCtx, MeCtx, navigate, Link, useRouter } from './router.jsx';
import { createRoot } from 'react-dom/client';
import './styles.css';
import { request, getSessionUser, signOut, pendingDevice, returnTo } from './api.js';
import { Login, Device } from './pages/auth.jsx';
import { Account, Tokens, Sessions, SessionDetail, Schedules, Shares } from './pages/user.jsx';
import { ShareView, ArtifactView, Remote } from './pages/public.jsx';
import { AdminOverview, AdminUsers, AdminUsage, AdminPlans, AdminSessions, AdminShares, AdminAudit } from './pages/admin.jsx';

const ROUTES = [
  ['/device', Device, 'auth'],
  ['/s/:id', ShareView, 'public'],
  ['/a/:slug', ArtifactView, 'public'],
  ['/remote/:room', Remote, 'auth'],
  ['/', Account, 'user'],
  ['/account', Account, 'user'],
  ['/tokens', Tokens, 'user'],
  ['/sessions/:id', SessionDetail, 'user'],
  ['/sessions', Sessions, 'user'],
  ['/schedules', Schedules, 'user'],
  ['/shares', Shares, 'user'],
  ['/admin/users', AdminUsers, 'admin'],
  ['/admin/usage', AdminUsage, 'admin'],
  ['/admin/plans', AdminPlans, 'admin'],
  ['/admin/sessions', AdminSessions, 'admin'],
  ['/admin/shares', AdminShares, 'admin'],
  ['/admin/audit', AdminAudit, 'admin'],
  ['/admin', AdminOverview, 'admin'],
];

function match(path) {
  for (const [pattern, Component, access] of ROUTES) {
    const keys = [];
    const re = new RegExp(`^${pattern.replace(/:(\w+)/g, (_, k) => { keys.push(k); return '([^/]+)'; })}/?$`);
    const m = re.exec(path);
    if (m) return { Component, access, params: Object.fromEntries(keys.map((k, i) => [k, decodeURIComponent(m[i + 1])])) };
  }
  return null;
}

function Layout({ me, children }) {
  const [open, setOpen] = useState(false);
  const { path } = useRouter();
  useEffect(() => setOpen(false), [path]);
  return (
    <div className="shell">
      <aside className={`side ${open ? 'open' : ''}`} onClick={e => e.target === e.currentTarget && setOpen(false)}>
        <div className="brand"><img src="/icon.svg" alt="" />Forge Console</div>
        <nav className="nav">
          <div className="nav-label">You</div>
          <Link to="/account">Plan &amp; usage</Link>
          <Link to="/sessions">Cloud sessions</Link>
          <Link to="/schedules">Routines</Link>
          <Link to="/shares">Shares</Link>
          <Link to="/tokens">Tokens</Link>
          {me.user.role === 'admin' && <>
            <div className="nav-label">Admin</div>
            <Link to="/admin">Overview</Link>
            <Link to="/admin/users">Users</Link>
            <Link to="/admin/usage">Usage &amp; models</Link>
            <Link to="/admin/plans">Plans &amp; quotas</Link>
            <Link to="/admin/sessions">All sessions</Link>
            <Link to="/admin/shares">All shares</Link>
            <Link to="/admin/audit">Audit log</Link>
          </>}
        </nav>
        <div className="side-foot">
          <div>{me.user.email}</div>
          <div className="muted">{me.plan.name} plan{me.user.role === 'admin' ? ' · admin' : ''}</div>
          <button className="btn" style={{ marginTop: 8 }} onClick={async () => { await signOut(); window.location.href = '/'; }}>Sign out</button>
        </div>
      </aside>
      <main className="main">
        <button className="btn menu-btn" onClick={() => setOpen(true)}>☰ Menu</button>
        {children}
      </main>
    </div>
  );
}

function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [me, setMe] = useState(undefined);
  const [error, setError] = useState(null);
  useEffect(() => { const on = () => setPath(window.location.pathname); window.addEventListener('popstate', on); return () => window.removeEventListener('popstate', on); }, []);

  const loadMe = useCallback(async () => {
    setError(null);
    try {
      if (!(await getSessionUser())) { setMe(null); return; }
      setMe(await request('/v1/me'));
      // Back from Google/GitHub in the middle of a Forge Code sign-in: continue to its authorization page.
      const code = pendingDevice.get();
      if (code && window.location.pathname !== '/device') navigate(`/device?code=${encodeURIComponent(code)}`);
      const back = returnTo.take();
      if (!code && back && window.location.pathname === '/') navigate(back);
    } catch (e) {
      if (e.status === 401) setMe(null); else { setError(e.message); setMe(null); }
    }
  }, []);
  useEffect(() => { loadMe(); }, [loadMe]);

  const route = match(path);
  const ctx = { path, navigate };
  let body;
  if (!route) body = <div className="center"><div className="card auth-card"><h1>Not found</h1><p className="muted">No page at {path}.</p><Link to="/">Go to the console</Link></div></div>;
  else if (route.access === 'public') body = <route.Component {...route.params} />;
  else if (me === undefined) body = <div className="center muted">Loading…</div>;
  else if (!me) body = <Login error={error} onSignedIn={loadMe} />;
  else if (route.access === 'auth') body = <MeCtx.Provider value={me}><route.Component {...route.params} /></MeCtx.Provider>;
  else if (route.access === 'admin' && me.user.role !== 'admin') body = <Layout me={me}><h1>Admins only</h1><p className="muted">Your account doesn't have the admin role.</p></Layout>;
  else body = <MeCtx.Provider value={me}><Layout me={me}><route.Component {...route.params} reloadMe={loadMe} /></Layout></MeCtx.Provider>;
  return <RouterCtx.Provider value={ctx}>{body}</RouterCtx.Provider>;
}

createRoot(document.getElementById('root')).render(<App />);
